import {setupFunctions} from "./consent-manager.js";
import {adjustPullElements} from "../forms/pull-offset-adjustments.js";
import {initHeaderLayout} from "../ui/header-layout.js";
import {initModalRelocator} from "../ui/modal-relocator.js";
import {initButtonContextStyler} from "../ui/button-context-styler.js";
import {
    changeNavLinksAfterLoad,
} from "./navigation.js";
import {initVenoBox, initVideoLightbox, initImageLightbox} from "../ui/lightbox-handling.js";
import {initCssAnimationPlayer} from "../ui/css-animation-player.js";
import {initSyndicationActions} from "../ui/syndication-actions.js";

const featureDefaults = {
    floatingLabels: false,
    lazyloadAssets: true,
    cssAnimations: true,
    popovers: true,
    tooltips: true,
    filepond: true,
};

const siteFeatures = {
    ...featureDefaults,
    ...(window.SiteFeatures || {}),
};

const isFeatureEnabled = (key) => Boolean(siteFeatures[key]);

if (typeof window !== 'undefined') {
    window.FilePondHelpers = window.FilePondHelpers || {};
    if (typeof window.FilePondHelpers.syncFormData !== 'function') {
        window.FilePondHelpers.syncFormData = () => {};
    }
}

const ensureLazyloadModules = () => {
    if (!isFeatureEnabled('lazyloadAssets')) {
        return Promise.resolve();
    }

    if (lazyloadModulesPromise) {
        return lazyloadModulesPromise;
    }

    lazyloadModulesPromise = Promise.all([
        import('../lazyload/lazyload-manager.js'),
        import('../lazyload/lazyload-slider.js'),
    ]).catch((error) => {
        console.warn('[lazyload] failed to register modules', error);
        lazyloadModulesPromise = null;
        return false;
    });

    return lazyloadModulesPromise;
};

const cssAnimationOptions = {
    allowRepeat: false,
};

const resizeHandlers = new Set();
const rootWindow = typeof window !== 'undefined' ? window : undefined;
let rotateImageCleanup = null;
let rotateImageLazyCleanup = null;
let mobileNavCleanup = null;
let conditionalAssetsCleanup = null;
let filepondLazyCleanup = null;
let lazyloadModulesPromise = null;
const ROTATE_SELECTOR = '.rotateImage';
const rotateImages = new Set();
let rotateObserver = null;
let rotateScrollHandler = null;
let rotateVisibleCount = 0;
let rotateTicking = false;
let rotateLastScrollTop = 0;

const safeInvoke = (fn) => {
    if (typeof fn !== 'function') {
        return undefined;
    }

    try {
        return fn();
    } catch (error) {
        console.error(`Error executing function ${fn.name || 'anonymous'}:`, error);
        return undefined;
    }
};

const scheduleAfterFrame = (fn) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        return window.setTimeout(() => safeInvoke(fn), 16);
    }

    return window.requestAnimationFrame(() => safeInvoke(fn));
};

const scheduleIdle = (fn, timeout = 1200) => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    if (typeof window.requestIdleCallback === 'function') {
        return window.requestIdleCallback(() => safeInvoke(fn), {timeout});
    }

    return window.setTimeout(() => safeInvoke(fn), timeout);
};

const initOnce = (fn) => {
    let pending = false;
    let executed = false;
    let result;

    return (...args) => {
        if (executed || pending) {
            return result;
        }

        result = fn(...args);

        if (result && typeof result.then === 'function') {
            pending = true;
            result = result.then(
                (value) => {
                    pending = false;
                    executed = true;
                    return value;
                },
                (error) => {
                    pending = false;
                    executed = false;
                    throw error;
                }
            );
            return result;
        }

        executed = true;
        return result;
    };
};

const scriptLoadRegistry = new Map();

const resolveCspNonce = () => {
    if (!rootWindow || !rootWindow.document) {
        return '';
    }

    const windowNonce = typeof rootWindow.__cspNonce === 'string' ? rootWindow.__cspNonce.trim() : '';
    if (windowNonce) {
        return windowNonce;
    }

    const scriptWithNonce = rootWindow.document.querySelector('script[nonce]');
    const scriptNonce = scriptWithNonce?.getAttribute('nonce')?.trim() || '';
    if (scriptNonce) {
        return scriptNonce;
    }

    const metaNonce = rootWindow.document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content')?.trim() || '';
    return metaNonce;
};

const applyCspNonceToScript = (script) => {
    if (!script || typeof script.setAttribute !== 'function') {
        return;
    }

    if (script.getAttribute('nonce')) {
        return;
    }

    const nonce = resolveCspNonce();
    if (nonce) {
        script.setAttribute('nonce', nonce);
    }
};

const ensureTrailingSlash = (value = '') => {
    if (!value) {
        return '';
    }
    return value.endsWith('/') ? value : `${value}/`;
};

const detectDistBasePathFromScripts = () => {
    if (!rootWindow || !rootWindow.document) {
        return '';
    }

    const scripts = Array.from(rootWindow.document.querySelectorAll('script[src]'));
    for (let index = scripts.length - 1; index >= 0; index -= 1) {
        const script = scripts[index];
        const src = script.getAttribute('src') || '';
        const match = src.match(/(\/files\/base\/layout\/js\/dist\/[^/]+\/)/);
        if (match?.[1]) {
            return ensureTrailingSlash(match[1]);
        }
    }

    return '';
};

const getPreferredDistBasePath = () => {
    const webpackPublicPath = typeof __webpack_public_path__ === 'string'
        ? ensureTrailingSlash(__webpack_public_path__)
        : '';

    if (webpackPublicPath) {
        return webpackPublicPath;
    }

    return detectDistBasePathFromScripts();
};

const buildVendorScriptCandidates = (fileName, legacyCandidates = []) => {
    const candidates = [];
    const distBasePath = getPreferredDistBasePath();

    if (distBasePath) {
        candidates.push(`${distBasePath}vendor/${fileName}`);
    }

    return Array.from(new Set([
        ...candidates,
        ...legacyCandidates,
    ]));
};

const loadScriptOnce = (src, {defer = true, async = false, attributes = {}} = {}) => {
    if (!rootWindow || !rootWindow.document) {
        return Promise.resolve();
    }

    if (scriptLoadRegistry.has(src)) {
        return scriptLoadRegistry.get(src);
    }

    const existing = rootWindow.document.querySelector(`script[src="${src}"]`);
    if (existing) {
        if (existing.dataset.vsmLoaded === 'true' || existing.readyState === 'complete') {
            const promise = Promise.resolve();
            scriptLoadRegistry.set(src, promise);
            return promise;
        }

        const promise = new Promise((resolve, reject) => {
            existing.addEventListener('load', () => {
                existing.dataset.vsmLoaded = 'true';
                resolve();
            }, {once: true});
            existing.addEventListener('error', (event) => {
                scriptLoadRegistry.delete(src);
                reject(event);
            }, {once: true});
        });
        scriptLoadRegistry.set(src, promise);
        return promise;
    }

    const script = rootWindow.document.createElement('script');
    script.src = src;
    applyCspNonceToScript(script);
    if (defer) {
        script.defer = true;
    }
    if (async) {
        script.async = true;
    }

    Object.entries(attributes || {}).forEach(([key, value]) => {
        if (typeof value === 'boolean') {
            if (value) {
                script.setAttribute(key, '');
            }
            return;
        }
        if (value !== null && typeof value !== 'undefined') {
            script.setAttribute(key, value);
        }
    });

    script.dataset.vsmLoader = 'true';

    const promise = new Promise((resolve, reject) => {
        script.addEventListener('load', () => {
            script.dataset.vsmLoaded = 'true';
            resolve();
        }, {once: true});
        script.addEventListener('error', (event) => {
            scriptLoadRegistry.delete(src);
            reject(event);
        }, {once: true});
    });

    scriptLoadRegistry.set(src, promise);
    rootWindow.document.head.appendChild(script);

    return promise;
};

const ensureFilePondIntegration = initOnce(() => {
    return import('../forms/filepond-integration.js').catch((error) => {
        console.warn('[FilePond] failed to load integration module', error);
    });
});

const findExistingSwiperScript = () => {
    if (!rootWindow || !rootWindow.document) {
        return null;
    }
    return rootWindow.document.querySelector('script[data-vsm-swiper], script[src*="swiper-bundle"]');
};

const ensureSwiperVendor = initOnce(() => {
    if (typeof rootWindow === 'undefined' || typeof rootWindow.document === 'undefined') {
        return Promise.resolve();
    }

    if (typeof rootWindow.Swiper !== 'undefined') {
        return Promise.resolve();
    }

    const existing = findExistingSwiperScript();
    if (existing) {
        if (!existing.dataset.vsmSwiper) {
            existing.dataset.vsmSwiper = 'true';
        }
        if (existing.dataset.vsmLoaded === 'true' || existing.readyState === 'complete') {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => {
                existing.dataset.vsmLoaded = 'true';
                resolve();
            }, {once: true});
            existing.addEventListener('error', reject, {once: true});
        });
    }

    const candidateSources = buildVendorScriptCandidates('swiper-bundle.min.js', [
        '/files/base/layout/js/dist/vendor/swiper-bundle.min.js',
    ]);

    let attempt = 0;

    const tryLoad = () => {
        if (attempt >= candidateSources.length) {
            throw new Error('Unable to load Swiper vendor script');
        }

        const src = candidateSources[attempt++];
        return loadScriptOnce(src).catch((error) => {
            if (attempt >= candidateSources.length) {
                throw error;
            }
            return tryLoad();
        });
    };

    return tryLoad();
});

const ensureSwiperHelpers = initOnce(() => {
    return ensureSwiperVendor()
        .then(() => Promise.all([
            import(/* webpackChunkName: "vsm-swiper-helpers" */ '../_helpers/vsm-swiper.js'),
            import(/* webpackChunkName: "vsm-swiper-helpers" */ '../_helpers/vsm-swiper-hooks.js'),
        ]))
        .then(() => {
            const autoInit = rootWindow?.VSM?.swiper?.autoInit;
            if (typeof autoInit === 'function') {
                autoInit();
            }
            return rootWindow?.VSM?.swiper;
        });
});

if (rootWindow) {
    const swiperLoader = () => ensureSwiperHelpers().catch((error) => {
        console.warn('[Swiper] Failed to load helpers', error);
        throw error;
    });

    rootWindow.VSM = rootWindow.VSM || {};
    rootWindow.VSM.loadSwiper = swiperLoader;
    rootWindow.VSM.swiper = rootWindow.VSM.swiper || {};
    rootWindow.VSM.swiper.load = swiperLoader;
}

const findExistingVenoBoxScript = () => {
    if (!rootWindow || !rootWindow.document) {
        return null;
    }

    return rootWindow.document.querySelector('script[data-vsm-venobox], script[src*="venobox"]');
};

const ensureVenoBoxVendor = initOnce(() => {
    if (typeof rootWindow === 'undefined' || !rootWindow.document) {
        return Promise.resolve();
    }

    if (typeof rootWindow.VenoBox !== 'undefined') {
        return Promise.resolve();
    }

    const existing = findExistingVenoBoxScript();
    if (existing) {
        if (!existing.dataset.vsmVenobox) {
            existing.dataset.vsmVenobox = 'true';
        }
        if (existing.dataset.vsmLoaded === 'true' || existing.readyState === 'complete') {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => {
                existing.dataset.vsmLoaded = 'true';
                resolve();
            }, {once: true});
            existing.addEventListener('error', reject, {once: true});
        });
    }

    const candidateSources = buildVendorScriptCandidates('venobox.min.js', [
        '/files/base/layout/js/dist/vendor/venobox.min.js',
    ]);

    let attempt = 0;

    const tryLoad = () => {
        if (attempt >= candidateSources.length) {
            throw new Error('Unable to load VenoBox vendor script');
        }

        const src = candidateSources[attempt++];
        return loadScriptOnce(src)
            .then(() => {
                const script = rootWindow.document.querySelector(`script[src="${src}"]`);
                if (script) {
                    script.dataset.vsmVenobox = 'true';
                }
            })
            .catch((error) => {
                if (attempt >= candidateSources.length) {
                    throw error;
                }
                return tryLoad();
            });
    };

    return tryLoad();
});

const ensureVenoBox = initOnce(() => ensureVenoBoxVendor());

if (rootWindow) {
    rootWindow.VSM = rootWindow.VSM || {};

    const venoLoader = () => ensureVenoBox().catch((error) => {
        console.warn('[VenoBox] Failed to load vendor script', error);
        throw error;
    });

    rootWindow.VSM.loadVenoBox = venoLoader;
    rootWindow.VSM.venobox = rootWindow.VSM.venobox || {};
    rootWindow.VSM.venobox.load = venoLoader;
}

export const setupLazyIntersectionInit = (selector, callback, {root = null, rootMargin = '200px', threshold = 0} = {}) => {
    if (typeof document === 'undefined') {
        return () => {};
    }

    const runCallback = () => safeInvoke(callback);

    if (typeof IntersectionObserver === 'undefined') {
        scheduleIdle(runCallback, 1200);
        return () => {};
    }

    let mutationObserver = null;

    const observer = new IntersectionObserver((entries, obs) => {
        if (entries.some(entry => entry.isIntersecting)) {
            obs.disconnect();
            mutationObserver?.disconnect();
            runCallback();
        }
    }, {root, rootMargin, threshold});

    let tracked = false;
    document.querySelectorAll(selector).forEach((element) => {
        tracked = true;
        observer.observe(element);
    });

    if (tracked) {
        return () => observer.disconnect();
    }

    mutationObserver = typeof MutationObserver !== 'undefined' && document.body
        ? new MutationObserver((mutations) => {
            let found = false;
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) {
                        return;
                    }
                    if (node.matches?.(selector)) {
                        observer.observe(node);
                        found = true;
                    } else if (node.querySelectorAll) {
                        node.querySelectorAll(selector).forEach((match) => {
                            observer.observe(match);
                            found = true;
                        });
                    }
                });
            });

            if (found) {
                mutationObserver.disconnect();
            }
        })
        : null;

    mutationObserver?.observe(document.body, {childList: true, subtree: true});

    return () => {
        observer.disconnect();
        mutationObserver?.disconnect();
    };
};

const ensureResizeHandler = (handler) => {
    if (!handler || resizeHandlers.has(handler)) {
        return;
    }
    resizeHandlers.add(handler);
};

export const initConditionalAssets = () => {
    if (isFeatureEnabled('lazyloadAssets')) {
        ensureLazyloadModules();
    }

    const cleanupFns = [];

    const ensureLightboxes = initOnce(() => {
        if (typeof VenoBox !== 'undefined') {
            initVenoBox();
            initVideoLightbox();
            initImageLightbox();
            return;
        }

        const loader = rootWindow?.VSM?.loadVenoBox;
        if (typeof loader === 'function') {
            return Promise.resolve(loader())
                .then(() => {
                    if (typeof VenoBox !== 'undefined') {
                        initVenoBox();
                        initVideoLightbox();
                        initImageLightbox();
                    }
                })
                .catch((error) => {
                    console.warn('Failed to load VenoBox dynamically', error);
                });
        }
    });

    const ensureSwipers = () => {
        return ensureSwiperHelpers()
            .then(() => {
                const autoInit = rootWindow?.VSM?.swiper?.autoInit;
                if (typeof autoInit === 'function') {
                    autoInit();
                }
            })
            .catch((error) => {
                console.warn('Failed to load Swiper dynamically', error);
            });
    };

    cleanupFns.push(setupLazyIntersectionInit('[data-lightbox], [class*="lightbox_"], .venobox, .js-lightbox, .js-video-lightbox, .js-image-lightbox', ensureLightboxes, {
        rootMargin: '150px',
        threshold: 0
    }));

    cleanupFns.push(setupLazyIntersectionInit('.swiper', ensureSwipers, {
        rootMargin: '0px 0px 200px 0px',
        threshold: 0
    }));

    return () => {
        cleanupFns.forEach((cleanup) => safeInvoke(cleanup));
    };
};

const initMobileNav = () => {
    const mobileNavElement = document.querySelector("#mobileNav");
    const triggerElement = document.querySelector('a[href="#mobileNav"]');
    const body = document.body;

    if (!mobileNavElement || !triggerElement) {
        return () => {};
    }

    const resolveMobileBreakpointQuery = () => {
        const attrValue = mobileNavElement.dataset.mobileNavBreakpoint
            || document.documentElement.dataset.mobileNavBreakpoint
            || '';
        const cssValue = typeof window !== 'undefined'
            ? getComputedStyle(document.documentElement).getPropertyValue('--mobile-nav-breakpoint').trim()
            : '';
        const raw = attrValue.trim() || cssValue || '1039px';

        if (!raw) {
            return '(max-width: 1039px)';
        }

        if (raw.includes('(') && raw.includes(')')) {
            return raw;
        }

        return raw.includes('max-width') ? `(${raw})` : `(max-width: ${raw})`;
    };

    const queryDefinition = resolveMobileBreakpointQuery();
    const mediaQuery = window.matchMedia ? window.matchMedia(queryDefinition) : null;
    const isMobileViewport = () => !mediaQuery || mediaQuery.matches;

    let drawer = null;
    let menuInstance = null;

    const ensureMenu = () => {
        if (drawer || typeof MmenuLight === 'undefined' || !isMobileViewport()) {
            return drawer;
        }

        try {
            menuInstance = new MmenuLight(mobileNavElement);
            menuInstance.navigation({theme: "dark"});
            drawer = menuInstance.offcanvas({position: 'right'});

            const closeButton = mobileNavElement.querySelector('.mmenu-close-btn');
            if (closeButton && !closeButton.dataset.mmenuCloseBound) {
                closeButton.dataset.mmenuCloseBound = '1';
                closeButton.addEventListener('click', (event) => {
                    event.preventDefault();
                    drawer?.close();
                });
            }

            mobileNavElement.querySelectorAll('a').forEach(item => {
                if (item.dataset.mmenuCloseBound === '1') {
                    return;
                }
                item.dataset.mmenuCloseBound = '1';
                item.addEventListener('click', () => drawer?.close());
            });
        } catch (error) {
            console.warn('MmenuLight initialization failed:', error);
        }

        return drawer;
    };

    const resetFallbackState = () => {
        mobileNavElement.classList.remove('is-open');
        document.documentElement.classList.remove('has-mobile-nav');
    };

    const toggleDrawer = (event) => {
        event.preventDefault();

        if (!isMobileViewport()) {
            return;
        }

        const instance = ensureMenu();

        if (instance) {
            body.classList.contains("mm-ocd-opened") ? instance.close() : instance.open();
            return;
        }

        mobileNavElement.classList.toggle('is-open');
        document.documentElement.classList.toggle('has-mobile-nav');
    };

    triggerElement.addEventListener('click', toggleDrawer);

    const handleMediaQueryChange = (event) => {
        if (event.matches) {
            ensureMenu();
            return;
        }
        if (drawer) {
            drawer.close();
        }
        resetFallbackState();
    };

    if (mediaQuery && typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleMediaQueryChange);
    } else if (mediaQuery && typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(handleMediaQueryChange);
    }

    if (isMobileViewport()) {
        ensureMenu();
    }

    return () => {
        triggerElement.removeEventListener('click', toggleDrawer);

        if (mediaQuery && typeof mediaQuery.removeEventListener === 'function') {
            mediaQuery.removeEventListener('change', handleMediaQueryChange);
        } else if (mediaQuery && typeof mediaQuery.removeListener === 'function') {
            mediaQuery.removeListener(handleMediaQueryChange);
        }

        resetFallbackState();
        drawer = null;
        menuInstance = null;
    };
};

const updateMegaMenuOffsets = () => {
    const navRoot = document.querySelector('#mainNav');
    if (!navRoot) {
        return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const navRect = navRoot.getBoundingClientRect();
    if (navRect.bottom < 0 || navRect.top > viewportHeight) {
        return;
    }

    const wrappers = navRoot.querySelectorAll('.level_2-wrapper');
    if (!wrappers.length) {
        return;
    }

    // Read-Phase: Alle Positionen messen
    const offsets = [];
    wrappers.forEach((wrapper) => {
        const parentItem = wrapper.closest('li');
        offsets.push({
            wrapper,
            left: parentItem ? parentItem.getBoundingClientRect().left : null
        });
    });

    // Write-Phase: Alle CSS-Properties setzen
    offsets.forEach(({wrapper, left}) => {
        if (left !== null) {
            wrapper.style.setProperty('--mega-menu-offset', `${left}px`);
        }
    });
};

// Optimierte Bildrotation
const ensureRotateObserver = () => {
    if (rotateObserver || typeof IntersectionObserver === 'undefined') {
        return rotateObserver;
    }

    rotateLastScrollTop = window.pageYOffset;
    rotateObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const target = entry.target;
            const wasVisible = target.dataset.rotateVisible === '1';
            if (entry.isIntersecting) {
                if (!wasVisible) {
                    target.dataset.rotateVisible = '1';
                    rotateVisibleCount += 1;
                }
            } else if (wasVisible) {
                delete target.dataset.rotateVisible;
                rotateVisibleCount = Math.max(0, rotateVisibleCount - 1);
            }
        });
        updateRotateScrollHandler();
    }, {
        rootMargin: '50px',
        threshold: 0,
    });

    return rotateObserver;
};

const updateRotateScrollHandler = () => {
    const activateScroll = rotateVisibleCount > 0 || typeof IntersectionObserver === 'undefined';

    if (activateScroll && !rotateScrollHandler) {
        rotateScrollHandler = () => {
            if (rotateTicking) {
                return;
            }
            rotateTicking = true;

            requestAnimationFrame(() => {
                const scrollTop = window.pageYOffset;
                if (scrollTop !== rotateLastScrollTop) {
                    const direction = scrollTop > rotateLastScrollTop ? 0.3 : -0.3;
                    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

                    // READ-Phase: Sichtbare Bilder ermitteln
                    const updates = [];
                    rotateImages.forEach((image) => {
                        if (typeof IntersectionObserver !== 'undefined' && image.dataset.rotateVisible !== '1') {
                            return;
                        }
                        const rect = image.getBoundingClientRect();
                        if (rect.bottom >= -100 && rect.top <= viewportHeight + 100) {
                            updates.push(image);
                        }
                    });

                    // WRITE-Phase: Transforms setzen
                    updates.forEach((image) => {
                        image.rotation = (image.rotation || 0) + direction;
                        image.style.transform = `rotate(${image.rotation}deg)`;
                    });

                    rotateLastScrollTop = Math.max(0, scrollTop);
                }
                rotateTicking = false;
            });
        };
        window.addEventListener('scroll', rotateScrollHandler, {passive: true});
    } else if (!activateScroll && rotateScrollHandler) {
        window.removeEventListener('scroll', rotateScrollHandler);
        rotateScrollHandler = null;
        rotateTicking = false;
    }
};

const rotateImage = () => {
    const images = document.querySelectorAll(ROTATE_SELECTOR);
    if (images.length === 0) {
        return () => {};
    }

    const observer = ensureRotateObserver();

    images.forEach((image) => {
        if (rotateImages.has(image)) {
            return;
        }

        image.rotation = image.rotation || 0;
        rotateImages.add(image);
        if (observer) {
            observer.observe(image);
        } else {
            // IntersectionObserver nicht verfügbar – alle Bilder als sichtbar behandeln
            if (image.dataset.rotateVisible !== '1') {
                image.dataset.rotateVisible = '1';
                rotateVisibleCount += 1;
            }
        }
    });

    updateRotateScrollHandler();

    return () => {
        images.forEach((image) => {
            if (!rotateImages.delete(image)) {
                return;
            }

            if (observer) {
                observer.unobserve(image);
            }

            if (image.dataset.rotateVisible === '1') {
                delete image.dataset.rotateVisible;
                rotateVisibleCount = Math.max(0, rotateVisibleCount - 1);
            }
        });

        if (rotateImages.size === 0) {
            if (observer) {
                observer.disconnect();
            }
            rotateObserver = null;
            rotateVisibleCount = 0;
        }

        updateRotateScrollHandler();
    };
};


const initializePopovers = () => {
    if (typeof bootstrap === 'undefined' || !bootstrap.Popover) {
        console.log('Bootstrap Popover not available');
        return;
    }

    const popoverTriggerList = [...document.querySelectorAll('[data-bs-toggle="popover"]')];
    if (popoverTriggerList.length === 0) return;

    popoverTriggerList.forEach((popoverTriggerEl) => {
        try {
            new bootstrap.Popover(popoverTriggerEl);
        } catch (error) {
            console.warn('Popover initialization failed:', error);
        }
    });
};

const initializeTooltips = () => {
    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) {
        console.log('Bootstrap Tooltip not available');
        return;
    }

    const tooltipTriggerList = [...document.querySelectorAll('[data-bs-toggle="tooltip"]')];
    if (tooltipTriggerList.length === 0) return;

    tooltipTriggerList.forEach((tooltipTriggerEl) => {
        try {
            new bootstrap.Tooltip(tooltipTriggerEl);
        } catch (error) {
            console.warn('Tooltip initialization failed:', error);
        }
    });
};

const loadSearchParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const escapeCss = (value) => {
        if (typeof value !== 'string') {
            return '';
        }

        if (window.CSS && typeof window.CSS.escape === 'function') {
            return window.CSS.escape(value);
        }

        return value.replace(/["\\]/g, '\\$&');
    };

    for (const [parameterName, parameterValue] of urlParams) {
        const escapedName = escapeCss(parameterName);
        if (!escapedName) {
            continue;
        }

        const inputField = document.querySelector(`input[name="${escapedName}"]`);
        if (inputField) {
            inputField.value = parameterValue;
        }
    }
};

const revealFormErrors = () => {
    const firstError = document.querySelector('p.error');
    if (!firstError) return;

    const form = firstError.closest('form');
    if (!form) return;

    let revealed = false;

    // Strategy 1: ce_rsce_tabs — Fehler in einem Tab-Pane
    const tabPane = form.closest('[data-tab-key]');
    const tabContainer = tabPane ? tabPane.closest('.ce_rsce_tabs') : null;
    if (tabPane && tabContainer && !tabPane.classList.contains('is-active')) {
        const tabKey = tabPane.dataset.tabKey;
        const navButton = tabContainer.querySelector(
            '[data-tabs-nav] [data-tab-key="' + tabKey + '"]'
        );
        if (navButton) {
            navButton.click();
            revealed = true;
        }
    }

    // Strategy 2: Filter-Buttons (rsce_ansprechpartner etc.)
    if (!revealed) {
        const filterTagContainer = form.closest('[data-filter-tag]');
        if (filterTagContainer) {
            const filterTag = filterTagContainer.dataset.filterTag;
            const section = filterTagContainer.closest('.rsce-ansprechpartner');
            if (section) {
                const btn = section.querySelector(
                    '.filter-btn[data-filter-tag="' + filterTag + '"]'
                );
                if (btn && !btn.classList.contains('active')) {
                    btn.click();
                    revealed = true;
                }
            }
        }
    }

    // Strategy 3: Bootstrap Tabs (data-bs-toggle="tab"/"pill")
    if (!revealed) {
        const bsTabPane = form.closest('.tab-pane');
        if (bsTabPane && !bsTabPane.classList.contains('active')) {
            const paneId = bsTabPane.id;
            if (paneId) {
                const trigger = document.querySelector(
                    '[data-bs-toggle="tab"][data-bs-target="#' + paneId + '"],' +
                    '[data-bs-toggle="pill"][data-bs-target="#' + paneId + '"],' +
                    '[data-bs-toggle="tab"][href="#' + paneId + '"],' +
                    '[data-bs-toggle="pill"][href="#' + paneId + '"]'
                );
                if (trigger) {
                    if (window.bootstrap && bootstrap.Tab) {
                        new bootstrap.Tab(trigger).show();
                    } else {
                        trigger.click();
                    }
                    revealed = true;
                }
            }
        }
    }

    // Strategy 4: Bootstrap Accordion/Collapse (inkl. initially-hidden Items)
    if (!revealed) {
        const accordionItem = form.closest('.accordion-item.initially-hidden');
        if (accordionItem) {
            const accordion = accordionItem.closest('.accordion');
            if (accordion) {
                const toggleBtn = accordion.parentElement
                    ? accordion.parentElement.querySelector('.toggle-accordion-items[data-target-accordion="#' + accordion.id + '"]')
                    : null;
                if (toggleBtn && !toggleBtn.classList.contains('showing-all')) {
                    toggleBtn.click();
                }
            }
        }

        const collapseEl = form.closest('.accordion-collapse, .collapse');
        if (collapseEl && !collapseEl.classList.contains('show')) {
            if (window.bootstrap && bootstrap.Collapse) {
                bootstrap.Collapse.getOrCreateInstance(collapseEl).show();
            } else {
                const triggerId = collapseEl.id;
                if (triggerId) {
                    const trigger = document.querySelector(
                        '[data-bs-toggle="collapse"][data-bs-target="#' + triggerId + '"]'
                    );
                    if (trigger) trigger.click();
                }
            }
            revealed = true;
        }
    }

    // Strategy 5: Bootstrap Modal
    if (!revealed) {
        const modalEl = form.closest('.modal');
        if (modalEl && !modalEl.classList.contains('show')) {
            if (window.bootstrap && bootstrap.Modal) {
                bootstrap.Modal.getOrCreateInstance(modalEl).show();
            }
            revealed = true;
        }
    }

    // Strategy 6: Services Overview (custom .active toggle)
    if (!revealed) {
        const serviceContent = form.closest('.service-content');
        const servicesOverview = serviceContent ? serviceContent.closest('.services-overview') : null;
        if (serviceContent && servicesOverview && !serviceContent.classList.contains('active')) {
            const index = serviceContent.dataset.index;
            if (index !== undefined) {
                const mobileBtn = servicesOverview.querySelector(
                    '.mobile-service-button[data-index="' + index + '"]'
                );
                const desktopLink = servicesOverview.querySelector(
                    '.service-item[data-index="' + index + '"] .service-link'
                );
                if (mobileBtn) {
                    mobileBtn.click();
                    revealed = true;
                } else if (desktopLink) {
                    desktopLink.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
                    revealed = true;
                }
            }
        }
    }

    // Strategy 7: Process Steps Slider
    if (!revealed) {
        const processStep = form.closest('.process-step');
        const slider = processStep ? processStep.closest('.process-steps-element--slider') : null;
        if (processStep && slider && !processStep.classList.contains('is-active')) {
            const stepIndex = processStep.dataset.stepIndex;
            if (stepIndex !== undefined) {
                const navItem = slider.querySelector(
                    '.process-steps__nav-item[data-step-target="' + stepIndex + '"]'
                );
                if (navItem) {
                    navItem.click();
                    revealed = true;
                }
            }
        }
    }

    // Zum Fehler scrollen nach Tab-/Filter-Wechsel
    if (revealed) {
        setTimeout(() => {
            const err = document.querySelector('p.error');
            if (err) {
                err.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 150);
    }
};

const scrollToCaptchaError = () => {
    const captchaError = document.querySelector('.widget-captcha.error');
    if (captchaError) {
        captchaError.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }
};

const criticalDomTasks = [
    () => setupFunctions(),
    () => adjustPullElements(),
    initHeaderLayout,
    initModalRelocator,
];

const frameDomTasks = [
    updateMegaMenuOffsets,
    () => isFeatureEnabled('cssAnimations') && initCssAnimationPlayer(cssAnimationOptions),
    changeNavLinksAfterLoad,
    initButtonContextStyler,
    initSyndicationActions
].filter(Boolean);

const deferredDomTasks = [
    revealFormErrors,
    () => {
        if (mobileNavCleanup) {
            mobileNavCleanup();
        }
        mobileNavCleanup = initMobileNav();
    },
    () => isFeatureEnabled('popovers') && initializePopovers(),
    () => isFeatureEnabled('tooltips') && initializeTooltips(),
    loadSearchParams,
    scrollToCaptchaError,
    () => {
        if (!isFeatureEnabled('filepond')) {
            return;
        }
        if (filepondLazyCleanup) {
            filepondLazyCleanup();
        }
        filepondLazyCleanup = setupLazyIntersectionInit(
            'input[type="file"][data-filepond="true"]',
            ensureFilePondIntegration,
            {rootMargin: '500px', threshold: 0}
        );
    },
    () => {
        if (!isFeatureEnabled('lazyloadAssets')) {
            return;
        }
        if (rotateImageLazyCleanup) {
            rotateImageLazyCleanup();
        }
        rotateImageLazyCleanup = setupLazyIntersectionInit('.rotateImage', () => {
            if (rotateImageCleanup) {
                rotateImageCleanup();
            }
            rotateImageCleanup = rotateImage();
        }, {rootMargin: '0px 0px 200px 0px'});
    },
    () => {
        if (!isFeatureEnabled('lazyloadAssets')) {
            return;
        }
        if (conditionalAssetsCleanup) {
            conditionalAssetsCleanup();
        }
        conditionalAssetsCleanup = initConditionalAssets();
    }
];

ensureResizeHandler(adjustPullElements);
ensureResizeHandler(updateMegaMenuOffsets);

const runCriticalDomTasks = () => {
    if (isFeatureEnabled('lazyloadAssets')) {
        ensureLazyloadModules();
    }
    criticalDomTasks.forEach(safeInvoke);

    scheduleAfterFrame(() => {
        frameDomTasks.forEach(safeInvoke);
    });

    deferredDomTasks.forEach(task => {
        scheduleIdle(task, 1500);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runCriticalDomTasks, {once: true});
} else {
    runCriticalDomTasks();
}

window.addEventListener('load', () => {
    scheduleIdle(updateMegaMenuOffsets, 250);
});

window.addEventListener('resize', () => {
    resizeHandlers.forEach(handler => {
        scheduleAfterFrame(handler);
    });
}, {passive: true});

window.addEventListener('beforeunload', () => {
    if (typeof rotateImageCleanup === 'function') {
        rotateImageCleanup();
        rotateImageCleanup = null;
    }
    if (typeof rotateImageLazyCleanup === 'function') {
        rotateImageLazyCleanup();
        rotateImageLazyCleanup = null;
    }
    if (typeof filepondLazyCleanup === 'function') {
        filepondLazyCleanup();
        filepondLazyCleanup = null;
    }
    if (typeof mobileNavCleanup === 'function') {
        mobileNavCleanup();
        mobileNavCleanup = null;
    }
    if (typeof conditionalAssetsCleanup === 'function') {
        conditionalAssetsCleanup();
        conditionalAssetsCleanup = null;
    }
});
